// =============================================================================
// MeuDriver — Hook: useNotificationPreferences (Batch K2)
// Leitura e atualização das preferências de notificação do usuário logado.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Tipo das preferências
// ---------------------------------------------------------------------------

export interface NotificationPreferences {
  id: string
  user_id: string
  email_enabled:           boolean
  email_booking_confirmed: boolean
  email_booking_reminder:  boolean
  email_booking_cancelled: boolean
  email_new_message:       boolean
  email_new_review:        boolean
  email_dispute_update:    boolean
  push_booking_confirmed:  boolean
  push_booking_reminder:   boolean
  push_new_message:        boolean
  push_dispute_update:     boolean
  whatsapp_enabled:        boolean
  created_at: string
  updated_at: string
}

export type NotificationPreferencesUpdate = Partial<
  Omit<NotificationPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'>
>

const QUERY_KEY = 'notification-preferences'

// ---------------------------------------------------------------------------
// Query: busca preferências do usuário autenticado
// ---------------------------------------------------------------------------

export function useNotificationPreferences() {
  const { user } = useAuth()

  return useQuery<NotificationPreferences | null>({
    queryKey: [QUERY_KEY, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle()

      if (error) throw error
      return (data as NotificationPreferences | null)
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  })
}

// ---------------------------------------------------------------------------
// Mutation: atualiza preferências (debounce feito no componente)
// ---------------------------------------------------------------------------

export function useUpdateNotificationPreferences() {
  const { user } = useAuth()
  const qc = useQueryClient()

  return useMutation<void, Error, NotificationPreferencesUpdate>({
    mutationFn: async (updates) => {
      if (!user?.id) throw new Error('Usuário não autenticado')

      // Upsert garante que a linha exista mesmo que o trigger ainda não tenha rodado
      const { error } = await supabase
        .from('notification_preferences')
        .upsert(
          { user_id: user.id, ...updates },
          { onConflict: 'user_id' },
        )

      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, user?.id] })
      toast.success('Preferências salvas')
    },
    onError: (err) => {
      console.error('[useUpdateNotificationPreferences]', err.message)
      toast.error('Erro ao salvar preferências')
    },
  })
}
